import { call, put, takeLatest } from "redux-saga/effects";

import { ToastError } from "../../components/Global/Toasts/Toasty";
import { makePayload, generateDefaultActions, requestSaga } from "../../shared-utility/store/utils/helpers";

import { BrokerDeal, DeserializedBrokerDeal } from "../../model/BrokerDeal";

import { getCachedBrokerDealsData, cacheBrokerDealsData } from "./cache";
import { authorizedRequest } from "utils/api";

export const BROKER_DASHBOARD_GET_DEALS = generateDefaultActions("BROKER_DASHBOARD_GET_DEALS");
export const getBrokerDeals = () => makePayload(BROKER_DASHBOARD_GET_DEALS.value);
export function* getBrokerDealsSaga() {
  try {
    const cachedResponse: DeserializedBrokerDeal[] = yield call(getCachedBrokerDealsData);

    if (cachedResponse) {
      yield put(BROKER_DASHBOARD_GET_DEALS.createSuccess(cachedResponse));
    } else {
      const response: DeserializedBrokerDeal[] = yield call(authorizedRequest, "/broker-deals", undefined, undefined, undefined, false);
      cacheBrokerDealsData(response);

      yield put(BROKER_DASHBOARD_GET_DEALS.createSuccess(response));
    }

    return { error: false };
  } catch (error) {
      yield put(BROKER_DASHBOARD_GET_DEALS.createFailure(error));

    return { error: true };
  }
}

const initialState = { isLoading: true };

interface BrokerDashboardReducerAction {
  type: string;
  payload: BrokerDeal[];
}

const fixKrazyKendoDate = (dateString: string): Date | null => {
  if (!dateString) {
    return null;
  }

  const timeOffset = new Date().getTimezoneOffset();
  const hours = -Math.trunc(timeOffset / 60);
  const minutes = Math.abs(timeOffset % 60);
  let hoursString;
  if (hours < -9) {
    hoursString = `${hours}`;
  } else if (hours < 0) {
    hoursString = `-0${-hours}`;
  } else if (hours < 10) {
    hoursString = `0${hours}`;
  } else {
    hoursString = `${hours}`;
  }
  const minutesString = minutes > 9 ? `${minutes}` : `0${minutes}`;
  const timeOffsetString = `${hoursString}:${minutesString}`;

  if (dateString.length === 10) {
    return new Date(`${dateString}T00:00:00.000${timeOffsetString}`);
  }

  if (dateString.length === 24) {
    return new Date(dateString.substring(0, 23) + timeOffsetString);
  }

  console.warn(`Date ${dateString} is in an unknown format and may not be displayed correctly.`);

  return new Date(dateString);
};

export function brokerDashboardReducer(state = initialState, action: BrokerDashboardReducerAction = { type: "", payload: [] }) {
  const { type, payload } = action;

  switch (type) {
    case BROKER_DASHBOARD_GET_DEALS.value:
      return { ...state, isLoading: true };
    case BROKER_DASHBOARD_GET_DEALS.success:
      const dscrRoundingPrecision = 2;
      const brokerDeals = payload?.map(deal => {
        return {
          ...deal,
          maturityDate: fixKrazyKendoDate(deal.maturityDate),
          closingDate: fixKrazyKendoDate(deal.closingDate),
          amortizationStartDate: fixKrazyKendoDate(deal.amortizationStartDate),
          purchaseDate: fixKrazyKendoDate(deal.purchaseDate),
          noiAsOfDate: fixKrazyKendoDate(deal.noiAsOfDate),
          occupancyAsOfDate: fixKrazyKendoDate(deal.occupancyAsOfDate),
          marketValueAsOfDate: fixKrazyKendoDate(deal.marketValueAsOfDate),
          dscrActual: Number(deal.dscrActual?.toFixed(dscrRoundingPrecision)) || null
        };
      });

      return { ...state, brokerDeals, isLoading: false };
    case BROKER_DASHBOARD_GET_DEALS.failure:
      ToastError("Couldn’t load deals");

      return { ...state, isLoading: false };
    default:
      return state;
  }
}

export function* brokerDashboardSaga() {
  yield takeLatest(BROKER_DASHBOARD_GET_DEALS.value, getBrokerDealsSaga);
}
